import { motion, AnimatePresence } from 'motion/react';
import { Button, ButtonProps } from 'antd';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FaCheck } from 'react-icons/fa6';
import { BsCamera } from 'react-icons/bs';
import { DeleteOutlined } from '@ant-design/icons';
import Flex from '../Flex';
import { useTranslation } from 'react-i18next';

export interface CaptureButtonProps extends ButtonProps {
  src: string;
  loading?: boolean;
  captureText: string;
  removeText: string;
  video?: boolean;
  time?: number;
  onRemove?: () => void;
  onConfirm?: () => void;
}

export function CaptureButton({
  video = false,
  className,
  loading,
  onClick,
  style = {},
  removeText,
  captureText,
  onRemove = undefined,
  onConfirm = undefined,
  time = 1000,
  ...rest
}: CaptureButtonProps) {

  const [t] = useTranslation(['face']);
  const [compact, setCompact] = useState(false);
  const [fileSelected, setFileSelected] = useState(false);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (fileSelected) {
      setFileSelected(false);
      if (onRemove) onRemove();
      return;
    }
    setCompact(true);
    if (onClick) {
      onClick(e);
    }

    setTimeout(() => setFileSelected(true), time);
    setTimeout(() => setCompact(false), time + 500);
  }, [fileSelected, setFileSelected, onRemove, onClick, time]);

  return (
    <AnimatePresence mode="wait">
      <Flex
        align="center"
        style={{
          left: '50%',
          bottom: '1.5rem',
          position: 'absolute',
          transform: 'translateX(-50%)',
          transformOrigin: 'center right',
        }}
      >
        <Button
          onClick={handleClick}
          disabled={loading}
          style={{
            height: '45px',
            minWidth: '45px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'ease-in-out 200ms',
            background: loading ? '#ddd' : '#fff',
            ...style
          }}
          className={classNames('overflow-hidden rounded-full p-0 text-zinc-900', className)}
          {...rest}
        >
          <div
            className={
              classNames(
                'flex items-center px-3 relative',
                compact ? 'gap-0' : fileSelected ? '!gap-1.5' : 'gap-2'
              )}>
            <div className="inline-flex items-center">
              {fileSelected ?
                <DeleteOutlined className="text-[18px] text-red-500" /> :
                (!video || !compact) ? <BsCamera className="text-[24px]" /> : ''
              }
              {video && !fileSelected && compact && (
                <span className="text-xs">
                  {t('face:messages.please-turn-your-head')}
                </span>
              )}
            </div>

            <motion.div
              key="cap-btn-text"
              initial="compact"
              animate={compact ? 'compact' : 'expand'}
              exit="compact"
              variants={{
                compact: { width: 0 },
                expand: { width: 'auto' }
              }}
              transition={{
                duration: 0.2,
                bounce: 0,
                delay: 0
              }}
              className="text-sm font-normal overflow-hidden">
              {fileSelected ? (
                <span className="text-red-500 font-medium">{removeText}</span>
              ) : (
                <span>{captureText}</span>
              )}
            </motion.div>
          </div>
        </Button>
        {fileSelected && <motion.div
          style={{
            transformOrigin: 'right',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex'
          }}
          key="confirm-capture-btn ant-btn"
          className=" relative border-none border-0"
          variants={{
            show: { width: 'auto', opacity: 1, transform: 'scale(1)' },
            hide: { width: 0, opacity: 0, transform: 'scale(0.6)' }
          }}
          initial="hide"
          animate={fileSelected ? 'show' : 'hide'}
          exit="exit"
        >
          <Button
            onClick={onConfirm}
            loading={loading}
            className="rounded-full"
            style={{
              height: '40px',
              width: '40px',
              maxWidth: '40px',
              maxHeight: '40px',
              minWidth: '40px',
              minHeight: '40px',
              display: 'inline-flex',
            }}
          >
            {!loading && <FaCheck
              style={{
                left: '50%',
                top: '50%',
                position: 'absolute',
                fontSize: '18px',
                transform: 'translate(-50%, -50%)'
              }}
              className="text-green-500" />}
          </Button>
          {/* {loading ? <Spinner /> : } */}
        </motion.div>}
      </Flex>
    </AnimatePresence>
  );
}
